import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Lock, UserPlus, LogIn, Leaf, ArrowLeft, CheckCircle } from 'lucide-react';
import { trackUserSignup, trackUserLogin } from '../utils/analytics';

const AuthPage = ({ prefilledEmail = '', guestPlants = [], onBack }) => {
  const [isLogin, setIsLogin] = useState(!prefilledEmail); // If email prefilled, show signup
  const [formData, setFormData] = useState({
    email: prefilledEmail || '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNameFields, setShowNameFields] = useState(false);
  
  const { login, register } = useAuth();

  useEffect(() => {
    // If we have a prefilled email, focus on password field
    if (prefilledEmail && !isLogin) {
      const passwordField = document.querySelector('input[name="password"]');
      if (passwordField) passwordField.focus();
    }
  }, [prefilledEmail, isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // For signup, if names aren't shown yet, show them
    if (!isLogin && !showNameFields) {
      setShowNameFields(true);
      return;
    }
    
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.email, formData.password);
        if (result.success) {
          trackUserLogin();
          
          // If user had guest plants, offer to import them
          if (guestPlants.length > 0) {
            // You could add logic here to import guest plants to the user's account
            localStorage.removeItem('guestPlants');
          }
        }
      } else {
        result = await register(formData);
        if (result.success) {
          trackUserSignup(prefilledEmail ? 'quick_signup' : 'standard');
          
          // Import guest plants if any
          if (guestPlants.length > 0) {
            // Add logic to import guest plants
            localStorage.removeItem('guestPlants');
          }
        }
      }

      if (!result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSwitchMode = () => {
    setIsLogin(!isLogin);
    setShowNameFields(false);
    setError('');
    if (!prefilledEmail) {
      setFormData({ email: '', password: '', firstName: '', lastName: '' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Back to Landing Button */}
        {onBack && (
          <button
            onClick={onBack}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-800 transition"
          >
            <ArrowLeft size={20} className="mr-1" />
            Back to home
          </button>
        )}

        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Leaf className="text-green-600 mr-2" size={32} />
              <h1 className="text-2xl font-bold text-gray-800">Plant Tracker</h1>
            </div>
            
            {!isLogin && (
              <div className="text-left bg-green-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-green-800 mb-2">
                  Your free account includes:
                </p>
                <ul className="space-y-1">
                  <li className="flex items-start gap-2 text-sm text-green-700">
                    <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>Unlimited plants tracking</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-green-700">
                    <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>Smart watering reminders</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-green-700">
                    <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>Growth tracking & notes</span>
                  </li>
                  {guestPlants.length > 0 && (
                    <li className="flex items-start gap-2 text-sm text-green-700">
                      <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <span>Import your {guestPlants.length} demo plants</span>
                    </li>
                  )}
                </ul>
              </div>
            )}
            
            <p className="text-gray-600">
              {isLogin ? 'Welcome back!' : 'Join 500+ plant parents'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email field - always shown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="your@email.com"
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password field - always shown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="••••••••"
                  autoComplete={isLogin ? "current-password" : "new-password"}
                />
              </div>
              {!isLogin && !showNameFields && (
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 6 characters
                </p>
              )}
            </div>

            {/* Name fields - only shown after first submit for signup */}
            {!isLogin && showNameFields && (
              <div className="grid grid-cols-2 gap-4 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="First"
                      autoFocus
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Last"
                    />
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  {isLogin ? (
                    <>
                      <LogIn className="mr-2" size={16} />
                      Sign In
                    </>
                  ) : showNameFields ? (
                    <>
                      <UserPlus className="mr-2" size={16} />
                      Complete Signup
                    </>
                  ) : (
                    <>
                      Continue
                    </>
                  )}
                </>
              )}
            </button>

            {/* Quick action text */}
            {!isLogin && !showNameFields && (
              <p className="text-xs text-center text-gray-500">
                Click continue to add your name and complete signup
              </p>
            )}
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={handleSwitchMode}
              className="text-green-600 hover:text-green-700 font-medium"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>

          {/* Trust badges */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex justify-center items-center gap-6 text-xs text-gray-500">
              <span>🔒 SSL Secured</span>
              <span>🚫 No Spam</span>
              <span>💚 Free Forever</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;